import { useRef, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { useQueryClient } from '@mint-lib/query-client';
import type {
  BaseFile,
  FileUploaderV2RefType,
  ReadyForUploadFile,
} from '@mint-lib/tw-components/patterns';
import { Chat } from '@mint-sdk/api-ai-assistant';

import { rejectDuplicateName } from '../../helpers/knowledge-base/uploadValidation.js';
import { useLoadDocumentMutation } from '../../queries/rag.js';

export type UploadResult = { uploaded: number; failed: number };

type Params = {
  existingFileNames: string[];
  folderId?: string | null;
};

export const useUploadDraft = ({ existingFileNames, folderId }: Params) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const queryClient = useQueryClient();
  const uploaderRef = useRef<FileUploaderV2RefType | null>(null);
  const { mutateAsync, isPending } = useLoadDocumentMutation();

  const [files, setFiles] = useState<BaseFile[]>([]);
  const [description, setDescription] = useState('');

  const readyFiles = files.filter((file): file is ReadyForUploadFile =>
    file.isReadyForUploadFile(),
  );

  const validate = rejectDuplicateName(
    existingFileNames,
    t('File name already exists'),
  );

  const uploadFile = (file: File) =>
    mutateAsync({
      file,
      description,
      ...(folderId ? { folder_id: folderId } : {}),
    });

  const upload = async (): Promise<UploadResult> => {
    if (!uploaderRef.current) return { uploaded: 0, failed: 0 };

    const results = await uploaderRef.current.uploadFiles(readyFiles);

    await queryClient.invalidateQueries({
      queryKey: Chat.listRagDocuments({}).queryKey,
    });

    const uploaded = results.filter(
      ({ status }) => status === 'fulfilled',
    ).length;
    const failed = results.length - uploaded;

    if (failed) {
      enqueueSnackbar({
        variant: 'error',
        subTitle: t('{{count}} file could not be uploaded', { count: failed }),
        removable: true,
      });
    } else {
      setDescription('');
      setFiles([]);
    }

    return { uploaded, failed };
  };

  return {
    uploaderRef,
    setFiles,
    description,
    setDescription,
    validate,
    uploadFile,
    upload,
    isReady: readyFiles.length > 0,
    isPending,
  };
};
